/**
 * THE SEVEN CHOICES (2026-10-30, the seven-industry pivot; docs/PLAN-seven-industries-20261030.md): law, marketing,
 * finance & CPA, real estate, insurance, property management and any business, in this order, in every place the site
 * offers industries. Each covers firms and functions: the firm itself and the in-house team inside any organization.
 *
 * One list: an industry's key is the open ROI report's (src/roi/types.ts ROI_INDUSTRY_KEYS, held equal in order by
 * tests/landing/industries.test.ts), its slug names its solutions page (/solutions/<slug>), and its label is how a menu,
 * a footer or a row of links says it. Any business is the report's `general` at /solutions/business.
 *
 * Plain data with no React and no styles: the site's menus (src/config/siteNavigation.ts) ride today's footer in the
 * first download and the new design's shell, and read their solutions places from here.
 */
import type { RoiIndustryKey } from '../roi/types';

export interface Industry {
  /** The open ROI report's key for the industry. */
  key: RoiIndustryKey;
  /** The last part of its solutions page's address: /solutions/<slug>. */
  slug: string;
  /** Its name in a menu, a footer or a row of links. */
  label: string;
}

export const INDUSTRIES: readonly Industry[] = [
  { key: 'law', slug: 'law', label: 'law' },
  { key: 'marketing', slug: 'marketing', label: 'marketing' },
  { key: 'finance', slug: 'finance', label: 'finance & CPA' },
  { key: 'real-estate', slug: 'real-estate', label: 'real estate' },
  { key: 'insurance', slug: 'insurance', label: 'insurance' },
  { key: 'property-management', slug: 'property-management', label: 'property management' },
  { key: 'general', slug: 'business', label: 'any business' },
];

/** An industry by its report key. */
export function industryOf(key: RoiIndustryKey): Industry {
  const found = INDUSTRIES.find((i) => i.key === key);
  if (!found) throw new Error(`unknown industry ${key}`);
  return found;
}

/** The industry whose solutions page an address segment names, or null. */
export function industryForSlug(slug: string): Industry | null {
  return INDUSTRIES.find((i) => i.slug === slug) ?? null;
}

/** An industry's solutions page: /solutions/<slug>. */
export function solutionsPath(key: RoiIndustryKey): string {
  return `/solutions/${industryOf(key).slug}`;
}

/**
 * The address the solutions page for marketing agencies and growing businesses had until 2026-10-30; it now opens the
 * page for any business (App.tsx sends it there, and the crawler copy at it says so).
 */
export const SOLUTIONS_MOVED_FROM = '/solutions/professional';
